import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formSchemaAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Plus, GripVertical, Trash2, Settings, Eye, Save, ArrowLeft,
  Type, Hash, AlignLeft, ChevronDown, ListChecks, CheckSquare, 
  Circle, Calendar, Clock, Paperclip, Copy, LayoutList, X, ChevronRight,
  PenTool, Images, Sparkles, HelpCircle, Edit, GitBranch, Calculator,
  Package, ShoppingCart, User, Star, Heart, MapPin, Phone, Mail,
  FileText, Tag, Bookmark, Flag, Bell, Truck, Gift, CreditCard,
  Scissors, Palette, Cake, Coffee, Home, Building, Briefcase,
  Shield, Zap, Music, Camera, Globe, Key, Award, Crown, Diamond,
  Flame, Leaf, Sun, Moon, Cloud, Umbrella, Anchor, Compass
} from 'lucide-react';
import { generateId, FIELD_TYPES } from '../../lib/utils';

// Icons available for section separators
const SECTION_ICONS = {
  '': null,
  'package': Package, 'shopping-cart': ShoppingCart, 'user': User, 'star': Star,
  'heart': Heart, 'map-pin': MapPin, 'phone': Phone, 'mail': Mail,
  'file-text': FileText, 'tag': Tag, 'bookmark': Bookmark, 'flag': Flag,
  'bell': Bell, 'truck': Truck, 'gift': Gift, 'credit-card': CreditCard,
  'scissors': Scissors, 'palette': Palette, 'cake': Cake, 'coffee': Coffee,
  'home': Home, 'building': Building, 'briefcase': Briefcase, 'shield': Shield,
  'zap': Zap, 'music': Music, 'camera': Camera, 'globe': Globe,
  'key': Key, 'award': Award, 'crown': Crown, 'diamond': Diamond,
  'flame': Flame, 'leaf': Leaf, 'sun': Sun, 'moon': Moon,
  'cloud': Cloud, 'umbrella': Umbrella, 'anchor': Anchor, 'compass': Compass,
  'calendar': Calendar, 'clock': Clock, 'sparkles': Sparkles,
};

// Helper function to format date
const formatDate = (isoDate) => {
  if (!isoDate) return 'Дата неизвестна';
  
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Если меньше часа назад
  if (diffMins < 60) {
    if (diffMins < 1) return 'только что';
    if (diffMins === 1) return '1 минуту назад';
    if (diffMins < 5) return `${diffMins} минуты назад`;
    return `${diffMins} минут назад`;
  }

  // Если меньше суток назад
  if (diffHours < 24) {
    if (diffHours === 1) return '1 час назад';
    if (diffHours < 5) return `${diffHours} часа назад`;
    return `${diffHours} часов назад`;
  }

  // Если меньше 7 дней назад
  if (diffDays < 7) {
    if (diffDays === 1) return 'вчера';
    return `${diffDays} дней назад`;
  }

  // Иначе показываем дату
  const day = date.getDate();
  const month = date.toLocaleString('ru-RU', { month: 'short' });
  const year = date.getFullYear();
  const currentYear = now.getFullYear();

  // Если текущий год, не показываем год
  if (year === currentYear) {
    return `${day} ${month}`;
  }

  return `${day} ${month} ${year}`;
};

const FIELD_ICONS = {
  text: Type,
  number: Hash,
  textarea: AlignLeft,
  select: ChevronDown,
  multi_select: ListChecks,
  checkbox: CheckSquare,
  radio: Circle,
  date: Calendar,
  time: Clock,
  file: Paperclip,
  image_gallery: Images,
  section: LayoutList,
};

// Sortable Field Item
function SortableField({ field, onEdit, onDelete, onDuplicate, isActive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = FIELD_ICONS[field.type] || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-3 p-3 bg-card border border-border rounded-md hover:border-primary/50 transition-colors ${
        isActive ? 'ring-2 ring-primary' : ''
      }`}
      data-testid={`field-item-${field.id}`}
    >
      <button
        className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm truncate">{field.label}</span>
          {field.required && <Badge variant="secondary" className="text-xs">*</Badge>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {FIELD_TYPES.find(t => t.type === field.type)?.label}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDuplicate(field)} title="Копировать поле">
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(field)} title="Настройки">
          <Settings className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(field.id)} title="Удалить">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Field Settings Panel
function FieldSettingsPanel({ field, onUpdate, onClose, allFields }) {
  const [localField, setLocalField] = useState(field);

  const handleChange = (key, value) => {
    setLocalField(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onUpdate(localField);
    onClose();
  };

  const addOption = () => {
    const newOption = { id: generateId(), label: '', value: '' };
    handleChange('options', [...(localField.options || []), newOption]);
  };

  const updateOption = (optionId, key, value) => {
    const updated = (localField.options || []).map(opt =>
      opt.id === optionId ? { ...opt, [key]: value } : opt
    );
    handleChange('options', updated);
  };

  const removeOption = (optionId) => {
    handleChange('options', (localField.options || []).filter(opt => opt.id !== optionId));
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="field-settings-panel">
        <SheetHeader>
          <SheetTitle>Настройки поля</SheetTitle>
          <SheetDescription>Настройте параметры поля формы</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Basic Settings */}
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Название поля</Label>
              <Input
                value={localField.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Введите название"
                data-testid="field-label-input"
              />
              <p className="text-xs text-muted-foreground">Заголовок над полем ввода в форме заказа</p>
            </div>

            <div className="space-y-2">
              <Label>Подсказка в поле (placeholder)</Label>
              <Input
                value={localField.placeholder || ''}
                onChange={(e) => handleChange('placeholder', e.target.value)}
                placeholder="Текст подсказки"
              />
              <p className="text-xs text-muted-foreground">Серый текст внутри пустого поля — показывает пример того, что нужно ввести. Например: «Иванов Иван», «+7 900 123-45-67»</p>
            </div>

            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={localField.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Описание для пользователя"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">Дополнительная подпись под полем — поясняет, зачем нужно это поле или как его заполнить</p>
            </div>

            {localField.type === 'section' && (
              <div className="space-y-3">
                <Label>Иконка разделителя</Label>
                <div className="grid grid-cols-8 gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleChange('icon', '')}
                    className={`p-2 rounded-md border text-xs text-center transition-colors ${!localField.icon ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 hover:bg-muted border-border'}`}
                  >
                    ✕
                  </button>
                  {Object.entries(SECTION_ICONS).filter(([k]) => k !== '').map(([key, IconComp]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => handleChange('icon', key)}
                      className={`p-2 rounded-md border flex items-center justify-center transition-colors ${localField.icon === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 hover:bg-muted border-border'}`}
                    >
                      <IconComp className="h-4 w-4" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Выберите иконку или ✕ чтобы убрать</p>
              </div>
            )}

            {localField.type !== 'section' && (
              <div className="flex items-center justify-between rounded-lg border p-4 bg-white dark:bg-muted/30 mt-3">
                <div className="space-y-1 pr-4">
                  <Label className="text-sm font-medium">Обязательное поле</Label>
                  <p className="text-xs text-muted-foreground leading-snug">Без заполнения этого поля заказ нельзя будет оформить</p>
                </div>
                <Switch
                  checked={localField.required}
                  onCheckedChange={(checked) => handleChange('required', checked)}
                  data-testid="field-required-switch"
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Type-specific Settings */}
          {['text', 'textarea'].includes(localField.type) && (
            <div className="space-y-4">
              <h4 className="font-medium">Валидация</h4>
              <div className="space-y-2">
                <Label>Regex (регулярное выражение)</Label>
                <Input
                  value={localField.regex || ''}
                  onChange={(e) => handleChange('regex', e.target.value)}
                  placeholder="^[a-zA-Z]+$"
                />
                <p className="text-xs text-muted-foreground">{"Шаблон для проверки введённых данных. Если значение не совпадает — поле не пройдёт валидацию. Например: ^[0-9]+$ — только цифры, ^.{3,}$ — минимум 3 символа"}</p>
              </div>
            </div>
          )}

          {localField.type === 'number' && (
            <div className="space-y-4">
              <h4 className="font-medium">Ограничения</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Минимум</Label>
                  <Input
                    type="number"
                    value={localField.min_value || ''}
                    onChange={(e) => handleChange('min_value', e.target.value ? Number(e.target.value) : null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Максимум</Label>
                  <Input
                    type="number"
                    value={localField.max_value || ''}
                    onChange={(e) => handleChange('max_value', e.target.value ? Number(e.target.value) : null)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Значение по умолчанию</Label>
                <Input
                  type="number"
                  value={localField.default_value || ''}
                  onChange={(e) => handleChange('default_value', e.target.value ? Number(e.target.value) : null)}
                />
              </div>
            </div>
          )}

          {['select', 'multi_select', 'radio'].includes(localField.type) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Варианты выбора</h4>
                <Button variant="outline" size="sm" onClick={addOption} data-testid="add-option-btn">
                  <Plus className="h-4 w-4 mr-1" /> Добавить
                </Button>
              </div>
              <div className="space-y-3">
                {(localField.options || []).map((option, index) => (
                  <div key={option.id} className="flex items-center gap-2 p-2 rounded-lg border border-border/60 bg-muted/20">
                    <span className="text-xs text-muted-foreground w-5 text-center shrink-0">{index + 1}</span>
                    <Input
                      value={option.label}
                      onChange={(e) => {
                        const val = e.target.value;
                        const updated = (localField.options || []).map(opt =>
                          opt.id === option.id ? { ...opt, label: val, value: val } : opt
                        );
                        handleChange('options', updated);
                      }}
                      placeholder={`Вариант ${index + 1}`}
                      className="flex-1 h-9"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive/70 hover:text-destructive shrink-0"
                      onClick={() => removeOption(option.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {localField.type === 'date' && (
            <div className="space-y-4">
              <h4 className="font-medium">Настройки даты</h4>
              {(() => {
                const otherCalendarField = allFields.find(f => f.id !== localField.id && f.type === 'date' && f.use_for_calendar);
                return (
                  <div className="flex items-center justify-between rounded-lg border p-3 bg-white dark:bg-muted/30">
                    <div className="space-y-0.5 pr-4">
                      <Label className="text-sm font-medium">Показывать в календаре</Label>
                      <p className="text-xs text-muted-foreground leading-snug">
                        {otherCalendarField && !localField.use_for_calendar
                          ? `Сейчас в календаре: «${otherCalendarField.label}». При включении — заменит его`
                          : 'Заказ будет отображаться в календаре по этой дате'}
                      </p>
                    </div>
                    <Switch
                      checked={localField.use_for_calendar || false}
                      onCheckedChange={(checked) => handleChange('use_for_calendar', checked)}
                    />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Conditional Logic */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-3 w-full rounded-lg border border-border p-3 bg-white dark:bg-background hover:bg-muted/50 transition-colors group">
              <div className="p-1.5 rounded-md bg-blue-500/10 shrink-0">
                <GitBranch className="h-4 w-4 text-blue-500" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="text-sm font-medium">Условная логика</h4>
                <p className="text-xs text-muted-foreground">Показать поле по условию</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 mb-4 ml-1 pl-4 border-l-2 border-blue-500/20 space-y-4">
              <p className="text-sm text-muted-foreground">
                Показывать это поле только если выполняется условие. Например: поле «Доставка» появится, только если в поле «Тип» выбрано «С доставкой»
              </p>
              <div className="space-y-2">
                <Label>Зависит от поля</Label>
                <Select
                  value={localField.conditional?.field_id || ''}
                  onValueChange={(value) => handleChange('conditional', { 
                    ...localField.conditional, 
                    field_id: value,
                    operator: localField.conditional?.operator || 'equals'
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите поле" />
                  </SelectTrigger>
                  <SelectContent>
                    {allFields
                      .filter(f => f.id !== localField.id)
                      .map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {localField.conditional?.field_id && (
                <>
                  <div className="space-y-2">
                    <Label>Условие</Label>
                    <Select
                      value={localField.conditional?.operator || 'equals'}
                      onValueChange={(value) => handleChange('conditional', {
                        ...localField.conditional,
                        operator: value,
                        ...(value === 'is_visible' || value === 'is_not_visible' ? { value: '' } : {})
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equals">Равно</SelectItem>
                        <SelectItem value="not_equals">Не равно</SelectItem>
                        <SelectItem value="contains">Содержит</SelectItem>
                        <SelectItem value="greater_than">Больше</SelectItem>
                        <SelectItem value="less_than">Меньше</SelectItem>
                        <SelectItem value="is_visible">Показывается</SelectItem>
                        <SelectItem value="is_not_visible">Не показывается</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {localField.conditional?.operator !== 'is_visible' && localField.conditional?.operator !== 'is_not_visible' && (
                    <div className="space-y-2">
                      <Label>Значение</Label>
                      <Input
                        value={localField.conditional?.value || ''}
                        onChange={(e) => handleChange('conditional', {
                          ...localField.conditional,
                          value: e.target.value
                        })}
                        placeholder="Значение для сравнения"
                      />
                    </div>
                  )}
                  {(localField.conditional?.operator === 'is_visible' || localField.conditional?.operator === 'is_not_visible') && (
                    <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 p-2.5 rounded-md">
                      Поле будет {localField.conditional?.operator === 'is_visible' ? 'показано' : 'скрыто'}, когда выбранное поле выше видимо на форме (его собственное условие выполняется)
                    </p>
                  )}
                </>
              )}
              {localField.conditional?.field_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleChange('conditional', null)}
                  className="text-destructive"
                >
                  Удалить условие
                </Button>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Calculation */}
          {localField.type === 'number' && (
            <>
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-3 w-full rounded-lg border border-border p-3 bg-white dark:bg-background hover:bg-muted/50 transition-colors group">
                  <div className="p-1.5 rounded-md bg-amber-500/10 shrink-0">
                    <Calculator className="h-4 w-4 text-amber-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-sm font-medium">Вычисление</h4>
                    <p className="text-xs text-muted-foreground">Авто-расчёт по формуле</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 mb-4 ml-1 pl-4 border-l-2 border-amber-500/20 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Формула для автоматического расчёта значения
                  </p>
                  <div className="space-y-2">
                    <Label>Формула</Label>
                    <Input
                      value={localField.calculation?.formula || ''}
                      onChange={(e) => handleChange('calculation', {
                        formula: e.target.value
                      })}
                      placeholder="sum(items.price * items.quantity)"
                    />
                  </div>
                  <div className="rounded-lg bg-muted/50 border border-border/60 p-3 space-y-2">
                    <p className="text-xs font-medium text-foreground/80">Примеры формул:</p>
                    <div className="space-y-1.5">
                      <div className="flex items-start gap-2">
                        <code className="text-xs bg-background px-1.5 py-0.5 rounded border border-border shrink-0">field1 + field2</code>
                        <span className="text-xs text-muted-foreground">— сумма двух полей</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-xs bg-background px-1.5 py-0.5 rounded border border-border shrink-0">value * 1.2</code>
                        <span className="text-xs text-muted-foreground">— наценка 20%</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-xs bg-background px-1.5 py-0.5 rounded border border-border shrink-0">price * quantity</code>
                        <span className="text-xs text-muted-foreground">— цена × количество</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-xs bg-background px-1.5 py-0.5 rounded border border-border shrink-0">sum(items.price)</code>
                        <span className="text-xs text-muted-foreground">— сумма всех значений</span>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}

          <div className="pt-4">
            <Button onClick={handleSave} className="w-full" data-testid="save-field-settings-btn">
              <Save className="h-4 w-4 mr-2" />
              Сохранить настройки
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Form Preview Component
function FormPreview({ fields, formName }) {
  return (
    <Card className="h-full overflow-hidden flex flex-col">
      <CardHeader className="pb-2 border-b bg-muted/30 shrink-0">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Предпросмотр</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 min-h-0">
        <ScrollArea className="h-[calc(100vh-138px)]">
          <div className="p-5">
            {/* Form header simulation */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-foreground">
                {formName || 'Название формы'}
              </h2>
              <div className="mt-1.5 h-0.5 w-12 bg-primary rounded-full" />
            </div>

            {fields.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                  <PenTool className="h-7 w-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground">Добавьте поля в форму</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Они появятся здесь</p>
              </div>
            ) : (
              <div className="space-y-0">
                {fields.map((field) => (
                  <PreviewField key={field.id} field={field} />
                ))}
              </div>
            )}

            {/* Submit button simulation */}
            {fields.length > 0 && (
              <div className="mt-8 pt-5 border-t border-border/50">
                <div className="h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">Отправить</span>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function PreviewField({ field }) {
  // Section renders as a visual divider
  if (field.type === 'section') {
    const SectionIcon = field.icon ? SECTION_ICONS[field.icon] : null;
    return (
      <div className="pt-6 pb-3 px-4 -mx-4 bg-muted/50">
        <div className="flex items-center gap-3">
          {SectionIcon && (
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <SectionIcon className="h-4 w-4 text-primary" />
            </div>
          )}
          <h3 className="text-sm font-semibold text-foreground whitespace-nowrap">{field.label}</h3>
          <div className="flex-1 border-t border-border" />
        </div>
        {field.description && (
          <p className={`text-xs text-muted-foreground mt-1.5 ${SectionIcon ? 'ml-11' : ''}`}>{field.description}</p>
        )}
      </div>
    );
  }

  const renderFieldInput = () => {
    switch (field.type) {
      case 'text':
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center">
            <span className="text-sm text-muted-foreground/60">{field.placeholder || 'Введите текст...'}</span>
          </div>
        );
      case 'number':
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center">
            <span className="text-sm text-muted-foreground/60">{field.placeholder || '0'}</span>
          </div>
        );
      case 'textarea':
        return (
          <div className="min-h-[80px] px-3 py-2 rounded-md border border-input bg-background">
            <span className="text-sm text-muted-foreground/60">{field.placeholder || 'Введите текст...'}</span>
          </div>
        );
      case 'select':
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center justify-between">
            <span className="text-sm text-muted-foreground/60">{field.placeholder || 'Выберите...'}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground/40" />
          </div>
        );
      case 'multi_select':
        return (
          <div className="space-y-2 py-1">
            {(field.options || []).length > 0 ? (
              field.options.slice(0, 4).map((opt) => (
                <div key={opt.id} className="flex items-center gap-2.5">
                  <div className="h-4 w-4 rounded border border-input bg-background shrink-0" />
                  <span className="text-sm text-foreground/80">{opt.label}</span>
                </div>
              ))
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="h-4 w-4 rounded border border-input bg-background shrink-0" />
                <span className="text-sm text-muted-foreground/60">Вариант</span>
              </div>
            )}
            {(field.options || []).length > 4 && (
              <p className="text-xs text-muted-foreground/50 pl-6.5">ещё {field.options.length - 4}...</p>
            )}
          </div>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2.5 py-1">
            <div className="h-4 w-4 rounded border border-input bg-background shrink-0" />
            <span className="text-sm text-foreground/80">{field.placeholder || field.label}</span>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2 py-1">
            {(field.options || []).length > 0 ? (
              field.options.map((opt) => (
                <div key={opt.id} className="flex items-center gap-2.5">
                  <div className="h-4 w-4 rounded-full border border-input bg-background shrink-0" />
                  <span className="text-sm text-foreground/80">{opt.label}</span>
                </div>
              ))
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="h-4 w-4 rounded-full border border-input bg-background shrink-0" />
                  <span className="text-sm text-muted-foreground/60">Вариант 1</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="h-4 w-4 rounded-full border border-input bg-background shrink-0" />
                  <span className="text-sm text-muted-foreground/60">Вариант 2</span>
                </div>
              </>
            )}
          </div>
        );
      case 'date':
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground/50" />
            <span className="text-sm text-muted-foreground/60">Выберите дату</span>
          </div>
        );
      case 'time':
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground/50" />
            <span className="text-sm text-muted-foreground/60">--:--</span>
          </div>
        );
      case 'file':
        return (
          <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/20 hover:bg-muted/30 transition-colors">
            <div className="flex flex-col items-center text-center gap-2">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Paperclip className="h-5 w-5 text-muted-foreground/60" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Нажмите для загрузки</p>
                <p className="text-[10px] text-muted-foreground/50 mt-0.5">или перетащите файл</p>
              </div>
            </div>
          </div>
        );
      case 'image_gallery':
        return (
          <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <Images className="h-3.5 w-3.5" />
              <span>Галерея фото с описаниями</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="aspect-square bg-muted/50 rounded-lg flex items-center justify-center border border-border/50">
                <Plus className="h-5 w-5 text-muted-foreground/40" />
              </div>
              <div className="aspect-square bg-muted/30 rounded-lg flex items-center justify-center border border-border/30">
                <Images className="h-4 w-4 text-muted-foreground/20" />
              </div>
              <div className="aspect-square bg-muted/20 rounded-lg flex items-center justify-center border border-border/20">
                <Images className="h-4 w-4 text-muted-foreground/10" />
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center">
            <span className="text-sm text-muted-foreground/60">{field.placeholder || 'Введите значение...'}</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-1.5 mb-5">
      <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
        {field.label}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {field.description && (
        <p className="text-xs text-muted-foreground leading-relaxed">{field.description}</p>
      )}
      {renderFieldInput()}
    </div>
  );
}

// Main Form Builder Page
export default function FormBuilderPage() {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSchema, setEditingSchema] = useState(null);
  const [schemaName, setSchemaName] = useState('');
  const [schemaDescription, setSchemaDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showFieldGuide, setShowFieldGuide] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadSchemas();
    loadTemplates();
  }, []);

  const loadSchemas = async () => {
    try {
      const res = await formSchemaAPI.getAll();
      setSchemas(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки схем');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await formSchemaAPI.getTemplates();
      setTemplates(res.data);
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
    }
  };

  const startNewSchema = () => {
    setEditingSchema(null);
    setSchemaName('');
    setSchemaDescription('');
    setFields([]);
    setIsEditing(true);
  };

  const startFromTemplate = (template) => {
    setEditingSchema(null);
    setSchemaName(template.name);
    setSchemaDescription(template.description);
    // Add IDs to fields
    const fieldsWithIds = template.fields.map(field => ({
      ...field,
      id: generateId(),
      options: field.options?.map(opt => ({
        ...opt,
        id: opt.id || generateId()
      }))
    }));
    setFields(fieldsWithIds);
    setShowTemplateDialog(false);
    setIsEditing(true);
    toast.success(`Шаблон "${template.name}" загружен`);
  };

  const startEditSchema = (schema) => {
    setEditingSchema(schema);
    setSchemaName(schema.name);
    setSchemaDescription(schema.description || '');
    setFields(schema.fields || []);
    setIsEditing(true);
  };

  const addField = (type) => {
    const newField = {
      id: generateId(),
      type,
      label: FIELD_TYPES.find(t => t.type === type)?.label || 'Новое поле',
      placeholder: '',
      description: '',
      required: false,
      default_value: null,
      options: ['select', 'multi_select', 'radio'].includes(type) ? [] : undefined,
      children: ['section'].includes(type) ? [] : undefined,
    };
    setFields([...fields, newField]);
    setEditingField(newField);
  };

  const updateField = (updatedField) => {
    // If use_for_calendar was enabled, disable it on all other date fields
    if (updatedField.use_for_calendar && updatedField.type === 'date') {
      setFields(fields.map(f => {
        if (f.id === updatedField.id) return updatedField;
        if (f.type === 'date' && f.use_for_calendar) return { ...f, use_for_calendar: false };
        return f;
      }));
    } else {
      setFields(fields.map(f => f.id === updatedField.id ? updatedField : f));
    }
  };

  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
  };

  const duplicateField = (field) => {
    const newField = {
      ...field,
      id: generateId(),
      label: `${field.label} (копия)`,
      options: field.options ? field.options.map(opt => ({ ...opt, id: generateId() })) : undefined,
    };
    const index = fields.findIndex(f => f.id === field.id);
    const updated = [...fields];
    updated.splice(index + 1, 0, newField);
    setFields(updated);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      setFields((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveSchema = async () => {
    if (!schemaName.trim()) {
      toast.error('Введите название формы');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: schemaName,
        description: schemaDescription,
        fields,
      };

      if (editingSchema) {
        await formSchemaAPI.update(editingSchema.id, data);
        toast.success('Форма обновлена');
      } else {
        await formSchemaAPI.create(data);
        toast.success('Форма создана');
      }
      
      // Reload schemas and return to list
      await loadSchemas();
      
      // Close the editor and return to list
      setEditingSchema(null);
      setSchemaName('');
      setSchemaDescription('');
      setFields([]);
      setIsEditing(false);
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deleteSchema = async (schemaId, event) => {
    // Prevent any event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      // Note: stopImmediatePropagation might not be available in all contexts
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    // Use a timeout to ensure touch events complete
    setTimeout(async () => {
      const confirmed = window.confirm('Удалить эту форму? Это действие нельзя отменить.');
      if (!confirmed) return;
      
      try {
        await formSchemaAPI.delete(schemaId);
        toast.success('Форма удалена');
        if (editingSchema?.id === schemaId) {
          setEditingSchema(null);
          setSchemaName('');
          setSchemaDescription('');
          setFields([]);
          setIsEditing(false);
        }
        await loadSchemas();
      } catch (error) {
        console.error('Delete error:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Ошибка удаления';
        toast.error(errorMessage);
      }
    }, 100);
  };

  // If no schema is being edited, show list
  if (!isEditing) {
    return (
      <div className="space-y-6" data-testid="form-builder-list">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Конструктор форм</h1>
            <p className="text-sm text-muted-foreground">Создавайте уникальные формы заказов</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button 
              variant="outline" 
              onClick={() => setShowTemplateDialog(true)} 
              data-testid="create-from-template-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Из шаблона
            </Button>
            <Button onClick={startNewSchema} data-testid="create-form-btn">
              <Plus className="h-4 w-4 mr-2" />
              Создать форму
            </Button>
          </div>
        </div>

        {/* Template Selection Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Выберите шаблон формы
              </DialogTitle>
              <DialogDescription>
                Готовые шаблоны помогут вам быстро создать форму для вашего бизнеса
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  className="cursor-pointer hover:border-primary transition-all hover:shadow-md"
                  onClick={() => startFromTemplate(template)}
                >
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="text-4xl">{template.icon}</div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription className="mt-1">
                          {template.description}
                        </CardDescription>
                        <div className="flex items-center gap-2 mt-3">
                          <Badge variant="secondary">{template.category}</Badge>
                          <Badge variant="outline">{template.fields.length} полей</Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p className="font-medium mb-2">Включенные поля:</p>
                      <div className="flex flex-wrap gap-1">
                        {template.fields.slice(0, 6).map((field, idx) => (
                          <span key={idx} className="bg-muted px-2 py-1 rounded text-[10px]">
                            {field.label}
                          </span>
                        ))}
                        {template.fields.length > 6 && (
                          <span className="text-muted-foreground px-2 py-1">
                            +{template.fields.length - 6} ещё
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Отмена
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-5 bg-muted rounded w-2/3" />
                  <div className="h-4 bg-muted rounded w-1/2 mt-2" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : schemas.length === 0 ? (
          <Card className="p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <PenTool className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-semibold">Нет созданных форм</h3>
              <p className="text-muted-foreground mt-2 mb-4">
                Создайте свою первую форму заказа или используйте готовый шаблон
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Из шаблона
                </Button>
                <Button onClick={startNewSchema}>
                  <Plus className="h-4 w-4 mr-2" />
                  Создать форму
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schemas.map((schema) => (
              <Card 
                key={schema.id} 
                className="transition-colors"
                data-testid={`schema-card-${schema.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div 
                      className="flex-1 min-w-0 cursor-pointer active:opacity-70" 
                      onClick={() => startEditSchema(schema)}
                      onTouchStart={(e) => {
                        // Prevent scroll selection
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onTouchCancel={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      <CardTitle className="text-lg">{schema.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {schema.description || 'Без описания'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        className="text-muted-foreground hover:text-primary rounded-md p-2 sm:p-1.5 transition-colors touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] flex items-center justify-center active:bg-primary/10"
                        onClick={(e) => { e.stopPropagation(); startEditSchema(schema); }}
                        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); startEditSchema(schema); }}
                        data-testid={`edit-schema-${schema.id}`}
                        type="button"
                        aria-label="Редактировать форму"
                      >
                        <Edit className="h-5 w-5 sm:h-4 sm:w-4 pointer-events-none" />
                      </button>
                      <button
                        className="text-muted-foreground hover:text-destructive rounded-md p-2 sm:p-1.5 transition-colors touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] flex items-center justify-center active:bg-destructive/10"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => deleteSchema(schema.id, e)}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteSchema(schema.id, e);
                      }}
                      data-testid={`delete-schema-${schema.id}`}
                      type="button"
                      aria-label="Удалить форму"
                    >
                      <Trash2 className="h-5 w-5 sm:h-4 sm:w-4 pointer-events-none" />
                    </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Badge variant="secondary">{schema.fields?.length || 0} полей</Badge>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="gap-1.5 cursor-help">
                            <Clock className="h-3 w-3" />
                            {formatDate(schema.updated_at)}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Последнее изменение</p>
                          <p className="text-xs font-medium">
                            {schema.updated_at ? new Date(schema.updated_at).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Неизвестно'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form Builder Editor
  return (
    <div className="space-y-6" data-testid="form-builder-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            className="shrink-0"
            onClick={() => {
              setEditingSchema(null);
              setSchemaName('');
              setSchemaDescription('');
              setFields([]);
              setIsEditing(false);
            }}
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-muted-foreground">
            {editingSchema ? 'Редактирование формы' : 'Новая форма'}
          </h1>
        </div>
        <Button onClick={saveSchema} disabled={saving} data-testid="save-schema-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </div>

      {/* Main Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Field Editor */}
        <div className="space-y-4">
          {/* Form Name & Description */}
          <Card className="border-border">
            <CardContent className="pt-3 pb-3 space-y-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Название формы</Label>
                <Input
                  value={schemaName}
                  onChange={(e) => setSchemaName(e.target.value)}
                  className="text-base font-semibold h-9 bg-background"
                  placeholder="Например: Заказ торта"
                  data-testid="schema-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Описание</Label>
                <Input
                  value={schemaDescription}
                  onChange={(e) => setSchemaDescription(e.target.value)}
                  className="text-sm h-8 bg-background"
                  placeholder="Краткое описание (необязательно)"
                />
              </div>
            </CardContent>
          </Card>

          {/* Field Types */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Добавить поле</CardTitle>
                  <CardDescription className="text-xs">Выберите тип поля для формы</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowFieldGuide(true)}
                  aria-label="Справка по типам полей"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <TooltipProvider delayDuration={300}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {FIELD_TYPES.map((fieldType) => {
                    const Icon = FIELD_ICONS[fieldType.type];
                    return (
                      <Tooltip key={fieldType.type}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-auto py-2 sm:py-3 flex flex-col gap-1 min-w-0"
                            onClick={() => addField(fieldType.type)}
                            data-testid={`add-field-${fieldType.type}`}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span className="text-[10px] sm:text-xs truncate w-full text-center">{fieldType.shortLabel || fieldType.label}</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          <p className="font-medium">{fieldType.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">{fieldType.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>

          {/* Fields List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Поля формы ({fields.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-border rounded-lg">
                  <LayoutList className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">Добавьте поля в форму</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {fields.map((field) => (
                        <SortableField
                          key={field.id}
                          field={field}
                          onEdit={setEditingField}
                          onDelete={deleteField}
                          onDuplicate={duplicateField}
                          isActive={activeId === field.id}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className="lg:sticky lg:top-4 lg:h-[calc(100vh-80px)]">
          <FormPreview fields={fields} formName={schemaName} />
        </div>
      </div>

      {/* Field Settings Sheet */}
      {editingField && (
        <FieldSettingsPanel
          field={editingField}
          onUpdate={updateField}
          onClose={() => setEditingField(null)}
          allFields={fields}
        />
      )}

      {/* Field Guide Dialog for Mobile */}
      <Dialog open={showFieldGuide} onOpenChange={setShowFieldGuide}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Справка по типам полей
            </DialogTitle>
            <DialogDescription>
              Подробное описание каждого типа поля и когда его использовать
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto px-6 pb-6 space-y-3" style={{ WebkitOverflowScrolling: 'touch' }}>
            {FIELD_TYPES.map((fieldType) => {
              const Icon = FIELD_ICONS[fieldType.type];
              return (
                <div key={fieldType.type} className="border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">{fieldType.label}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{fieldType.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="border-t px-6 py-4">
            <Button onClick={() => setShowFieldGuide(false)} className="w-full">
              Понятно
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
